// Findings: the queue the team works, as cases (components/FindingsConsole.jsx).
// The page reads what triage escalated in the range (the findings a standard
// met) and the findings of every open investigation, narrowed by the scope a
// link carries and by the Explore clauses in the console's search; the console
// applies the search's case fields itself. Only staff listing what review
// cleared read every finding, the newest first, a page at a time.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import FindingsConsole from "../components/FindingsConsole.jsx";
import UnavailableLinkPage from "../components/UnavailableLinkPage.jsx";
import { useTriageReads } from "../components/lenses/ReviewsBody.jsx";
import { decodeScopePath, encodeScopePath } from "../lib/dimensionDefs.js";
import { keepReads, keptReads } from "../lib/keptTriageReads.js";
import { useMyPermissions } from "../lib/permissions.js";
import { ROUTER_PARAM } from "../lib/routeModel.js";
import { openCurrentOrganizationHome } from "../lib/tenantLink.js";
import { triageScopeToken } from "../lib/triagePage.js";
import { toApiRange } from "../lib/shell.js";
import { CONDUCT_PAGE } from "../lib/scopeReviews.js";
import { CONDUCT_FINDINGS_LIMIT } from "../lib/conductTypes.js";
import { useConsole } from "../lib/findingsConsoleStore.js";
import { listOpenCaseFindingIds } from "../lib/useConsoleInvestigations.js";

// The reads the console shows: these once they are complete, or refused;
// while they are still being read, the last complete reads of the same view
// (lib/keptTriageReads.js), marked `refreshing`. The console reads data from
// them, never their actions, so reads kept from an earlier visit are safe to
// show.
function useShownReads(t, key) {
    const complete = t.read && !t.failed;
    useEffect(() => { if (complete) keepReads(key, t); }, [complete, key, t]);
    const pending = !t.read && !t.failed && ["idle", "loading", "done"].includes(t.conductState.status);
    if (!pending) return t;
    const was = keptReads(key);
    return was ? { ...was, refreshing: true } : t;
}

// The findings of open investigations, read once a visit: null while read.
function useOpenCaseFindings(enabled) {
    const [ids, setIds] = useState(null);
    useEffect(() => {
        if (!enabled) return undefined;
        const ac = new AbortController();
        listOpenCaseFindingIds({ signal: ac.signal }).then(setIds, (err) => {
            if (ac.signal.aborted) return;
            console.warn("Findings could not read the open investigations; cases triage no longer escalates may be missing.", err);
            setIds([]);
        });
        return () => ac.abort();
    }, [enabled]);
    return ids;
}

function TriageData({ range, scopePath }) {
    const caps = useMyPermissions();
    const readRange = useMemo(() => toApiRange(range), [range]);
    const [limit, setLimit] = useState(CONDUCT_PAGE);
    const [before, setBefore] = useState(null);
    const everything = useConsole().cleared;
    const keepIds = useOpenCaseFindings(!everything);
    const escalated = useMemo(() => (everything ? null : { keepIds }), [everything, keepIds]);
    const t = useTriageReads({ scopePath, range: readRange, caps, followRuns: false, findingsLimit: limit, escalated });
    const more = everything && t.read && !t.failed && !!t.conductState?.clipped && limit < CONDUCT_FINDINGS_LIMIT;
    const loadOlder = useCallback(() => {
        setBefore({ reads: t });
        setLimit((n) => Math.min(n + CONDUCT_PAGE, CONDUCT_FINDINGS_LIMIT));
    }, [t]);
    const loadingOlder = !!before && !t.read && !t.failed;
    const read = useMemo(
        () => ({ ...(loadingOlder ? { ...before.reads, loadingOlder: true } : more ? { ...t, loadOlder } : t), keepIds }),
        [loadingOlder, before, t, more, loadOlder, keepIds],
    );
    const shown = useShownReads(read, `${encodeScopePath(scopePath) || "org"}|${range}`);
    return <FindingsConsole t={shown} caps={caps} scopePath={scopePath} range={range} />;
}

export default function TriagePage({ range = "1M" }) {
    const [params, setParams] = useSearchParams();
    const token = triageScopeToken(params);
    const scopePath = useMemo(() => decodeScopePath(token) || [], [token]);
    useEffect(() => {
        if (params.get("view") !== "scope.reviews") return;
        setParams((current) => {
            const next = new URLSearchParams(current);
            next.set("mode", "triage"); next.set("adminView", "triage"); next.delete("view");
            if (token) next.set(ROUTER_PARAM.TRIAGE_SCOPE, token);
            return next;
        }, { replace: true });
    }, [params, token, setParams]);
    if (token && !scopePath.length) return <UnavailableLinkPage onOpenDashboard={openCurrentOrganizationHome} />;
    return <TriageData key={token + "|" + range} range={range} scopePath={scopePath} />;
}
